/**
 * Lightweight length / area helpers for live picker feedback. Measurements
 * are computed in the project's native CRS (we picked in world coords) so
 * they're already in metres if the project SRID is metric — most commonly
 * UTM. For geographic CRSs (EPSG:4326 etc.) the numbers would be in
 * degrees; the toolbar callers only invoke these for projects with a real
 * metric SRID, so we don't try to detect / convert here.
 */

type Vec3 = [number, number, number];

const segmentLength = (a: Vec3, b: Vec3) => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

/** Sum of consecutive segment lengths in 3D. */
export const polylineLength = (verts: Vec3[]): number => {
  let total = 0;
  for (let i = 1; i < verts.length; i++) total += segmentLength(verts[i - 1], verts[i]);
  return total;
};

/** Polyline length plus the closing edge back to the first vertex. */
export const ringPerimeter = (ring: Vec3[]): number => {
  if (ring.length < 2) return 0;
  return polylineLength(ring) + segmentLength(ring[ring.length - 1], ring[0]);
};

/**
 * 2D ring area via the shoelace formula, projected to world XY. Z is
 * ignored — fine for ground / roof footprints (the dominant case for the
 * polygon tool); near-vertical rings would need an in-plane basis (see
 * GeoJson.tsx triangulation) but we're not measuring those in v1.
 */
export const ringArea2D = (ring: Vec3[]): number => {
  const n = ring.length;
  if (n < 3) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % n];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
};

const fmtNum = (n: number, digits = 2) =>
  n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 });

/** Auto-units: m → km when ≥ 1000 m. */
export const formatLength = (metres: number): string => {
  if (!Number.isFinite(metres) || metres <= 0) return '0 m';
  if (metres >= 1000) return `${fmtNum(metres / 1000, 3)} km`;
  return `${fmtNum(metres, 2)} m`;
};

/** Auto-units: m² → ha when ≥ 10 000 m², → km² when ≥ 1 000 000 m². */
export const formatArea = (sqm: number): string => {
  if (!Number.isFinite(sqm) || sqm <= 0) return '0 m²';
  if (sqm >= 1_000_000) return `${fmtNum(sqm / 1_000_000, 3)} km²`;
  if (sqm >= 10_000) return `${fmtNum(sqm / 10_000, 3)} ha`;
  return `${fmtNum(sqm, 2)} m²`;
};
